/**
 * O plano de uma tarefa, aberto: os passos em ordem, com estado e desfecho.
 *
 * ## O que esta lista precisa responder sem ninguém perguntar
 *
 * Três coisas, e é por elas que cada passo tem forma, palavra e cor:
 *
 *  · **Onde está o trabalho agora.** Um passo `executando` no meio de cinco é a
 *    única linha que importa naquele segundo, e ela tem que se achar de longe.
 *  · **O que já não vai acontecer.** `falhou` e `dispensado` são os dois jeitos
 *    de um passo terminar sem ser feito, e são coisas MUITO diferentes: um é um
 *    problema esperando decisão, o outro é uma decisão já tomada. Se os dois
 *    forem cinza-com-um-símbolo, a pessoa lê "cinco passos, três não deram" e
 *    perde a diferença entre um plano com defeito e um plano podado de
 *    propósito. Por isso o que falhou **grita** — cor quente, faixa na lateral,
 *    fundo tingido — e o que foi dispensado **sai de cena** — apagado, riscado,
 *    sem faixa nenhuma. Um chama, o outro se afasta.
 *  · **Se dá para acreditar no "feito".** Passo de ferramenta que rodou tem
 *    execução gravada, com trilha e evidência; dizer "feito" sem apontar para
 *    ela seria pedir fé. O selo de evidência só aparece quando ela existe.
 *
 * ## Um botão, e só num lugar
 *
 * O plano tem ordem, e ordem que a interface não respeita não é ordem. Só o
 * próximo passo executável ganha botão; os outros são leitura. E o rótulo diz o
 * que vai acontecer de verdade — num passo `humano` ninguém executa nada, a
 * pessoa vai lá e marca que fez, e escrever "Executar" ali seria a interface
 * prometendo um trabalho que ela não faz.
 *
 * ## O outro botão, que não obedece à ordem
 *
 * "Dispensar" aparece em TODO passo ainda aberto — `pendente` ou `falhou` —, e
 * essa assimetria é a coisa mais importante desta tela depois da lista em si.
 *
 * Executar obedece à ordem porque o trabalho tem ordem: o passo 3 depende do
 * que o 2 deixou pronto. Decidir que algo não vai ser feito não depende de
 * nada. Amarrar a dispensa ao próximo executável obrigaria a pessoa a
 * atravessar o plano inteiro para podar o último passo — ou pior, a executar o
 * passo 2 só para poder tirar o 3 —, que é a interface obrigando alguém a
 * fazer o que ela mesma decidiu não fazer.
 *
 * No passo que falhou, este é o único botão, e é a saída do beco: até ele
 * existir, um passo impossível ficava aberto para sempre e a única forma de
 * encerrar o plano era mentir dizendo "feito".
 *
 * Não aparece em `executando` (pode haver escrita em voo, e podar por cima
 * mandaria para o lado apagado da tela algo que talvez esteja acontecendo) nem
 * em `feito`/`dispensado` (já terminaram). Quem recusa de verdade é o
 * servidor, com frase própria para cada caso; aqui a regra é só para não
 * oferecer o que vai ser recusado.
 */

import type { PassoNaTela } from './usePainel';

/**
 * O estado do passo em três canais ao mesmo tempo: forma, palavra e cor.
 *
 * Os três de propósito. Cor sozinha exclui quem não a distingue; símbolo
 * sozinho é charada; palavra sozinha obriga a ler cinco linhas para achar a que
 * está acontecendo. Juntos, o olho pega a linha certa antes de ler qualquer
 * coisa — e quem usa leitor de tela recebe a explicação inteira.
 */
const ESTADOS: Readonly<
  Record<string, { readonly rotulo: string; readonly explicacao: string; readonly marca: string }>
> = {
  pendente: { rotulo: 'Pendente', explicacao: 'ainda não começou', marca: '○' },
  executando: { rotulo: 'Executando', explicacao: 'acontecendo agora', marca: '◐' },
  feito: { rotulo: 'Feito', explicacao: 'concluído', marca: '✓' },
  falhou: {
    rotulo: 'Falhou',
    explicacao: 'tentou e não deu certo; ainda espera uma decisão',
    marca: '✕',
  },
  dispensado: {
    rotulo: 'Dispensado',
    explicacao: 'tirado do plano de propósito; não será executado',
    marca: '—',
  },
};

const DESCONHECIDO = { rotulo: '', explicacao: 'estado que esta tela não conhece', marca: '·' };

/**
 * Qual passo pode rodar agora — ou nenhum.
 *
 * Nada roda enquanto alguma coisa está rodando: dois botões apertados em
 * sequência virariam duas execuções concorrentes sobre o mesmo plano, e a
 * segunda chegaria antes de a primeira ter escrito o que descobriu.
 *
 * Um passo que falhou não trava o resto. É a escolha menos ruim das duas:
 * travar deixaria a tarefa sem saída nenhuma (o passo que falhou não é mais
 * `pendente`, então não haveria o que apertar), e a falha continua gritando
 * logo acima — quem seguir, segue vendo.
 *
 * Um passo DISPENSADO no meio também não trava, e por construção: a busca é
 * pelo próximo `pendente`, não pelo próximo da ordem. Dispensar o passo 2 de
 * três faz o botão aparecer no 3 na mesma carga, sem renumerar nada e sem
 * caso especial aqui. Se um dia esta função passar a andar pela ordem em vez
 * de procurar por estado, é este parágrafo que ela quebra — um plano podado no
 * meio ficaria parado para sempre, com o botão em cima de um passo que
 * alguém decidiu não fazer.
 */
export function proximoExecutavel(passos: readonly PassoNaTela[]): string | null {
  if (passos.some((p) => p.estado === 'executando')) return null;
  return passos.find((p) => p.estado === 'pendente')?.id ?? null;
}

export function Passos({
  id,
  passos,
  executavelId,
  disparados,
  dispensando,
  onExecutar,
  onDispensar,
}: {
  /** ligado ao `aria-controls` do triângulo que abriu esta lista */
  id: string;
  passos: readonly PassoNaTela[];
  executavelId: string | null;
  /** passos cujo pedido já subiu e cuja resposta ainda não voltou */
  disparados: ReadonlySet<string>;
  /**
   * O mesmo, para a dispensa — e um conjunto separado de propósito.
   *
   * Juntar os dois pareceria economia e mentiria: `disparados` é o que decide
   * se o botão de executar aparece, e um passo `falhou` esperando dispensa
   * entraria nessa conta e ganharia um "Executando…" que ninguém pediu.
   */
  dispensando: ReadonlySet<string>;
  onExecutar: (passoId: string) => void;
  onDispensar: (passoId: string) => void;
}): JSX.Element {
  return (
    <ol className="u1-passos" id={id}>
      {passos.map((passo, i) => {
        const visual = ESTADOS[passo.estado] ?? {
          ...DESCONHECIDO,
          rotulo: passo.estado,
        };
        // Ocupado é "já pedi e ainda não sei": ou o servidor já disse
        // `executando`, ou o clique acabou de sair daqui. Nos dois casos apertar
        // de novo só produziria uma segunda execução do mesmo passo.
        const ocupado = passo.estado === 'executando' || disparados.has(passo.id);
        // O botão aparece no passo executável E no que está rodando — sumir com
        // ele no instante do clique tiraria da tela justamente o lugar que diz
        // "é aqui que está acontecendo".
        const temBotao = passo.id === executavelId || ocupado;
        // Aberto é o que ainda pode virar outra coisa. A lista é explícita, e
        // não um `!== 'feito' && !== 'dispensado'`: assim um sexto estado que
        // apareça um dia nasce sem botão, em vez de herdar um que ninguém
        // pensou para ele.
        const aberto = passo.estado === 'pendente' || passo.estado === 'falhou';
        const dispensaEmVoo = dispensando.has(passo.id);
        const podeDispensar = (aberto && !ocupado) || dispensaEmVoo;

        return (
          <li key={passo.id} className={`u1-passo u1-passo--${passo.estado}`}>
            <span className="u1-passo__marca" aria-hidden="true">
              {visual.marca}
            </span>

            <div className="u1-passo__corpo">
              <p className="u1-passo__descricao">{passo.descricao}</p>

              <p className="u1-passo__meta">
                <span className="u1-passo__estado">
                  {visual.rotulo}
                  {/* Como no selo dos cartões: o rótulo curto é para o olho, o
                      significado é para quem escuta a tela. */}
                  <span className="u1-oculto">: {visual.explicacao}</span>
                </span>

                {passo.tipo === 'ferramenta' && passo.ferramenta !== null && (
                  // Tracejado: isto é o que ele VAI usar, não o que ficou
                  // registrado. O selo de evidência ao lado é sólido, e a
                  // diferença entre as duas bordas é a diferença entre plano e
                  // registro.
                  <span className="u1-passo__ferramenta">
                    <span className="u1-oculto">ferramenta: </span>
                    {passo.ferramenta}
                  </span>
                )}

                {passo.tipo === 'humano' && <span className="u1-passo__quem">depende de gente</span>}

                {passo.execucaoId !== null && (
                  <span
                    className="u1-passo__evidencia"
                    title="a execução deste passo ficou registrada, com trilha e evidência"
                  >
                    evidência
                    <span className="u1-oculto"> registrada para este passo</span>
                  </span>
                )}
              </p>

              {passo.resultado !== null && (
                <p className="u1-passo__resultado">{passo.resultado}</p>
              )}

              {(temBotao || podeDispensar) && (
                <p className="u1-passo__acoes">
                  {temBotao && (
                    <button
                      type="button"
                      className="u1-passo__executar"
                      onClick={() => onExecutar(passo.id)}
                      // Também desligado enquanto uma dispensa deste mesmo
                      // passo está em voo: são duas decisões opostas sobre uma
                      // linha só. O servidor resolve a corrida — uma das duas
                      // perde no UPDATE condicional —, mas oferecer as duas ao
                      // mesmo tempo é a tela convidando para ela.
                      disabled={ocupado || dispensaEmVoo}
                    >
                      {ocupado
                        ? 'Executando…'
                        : passo.tipo === 'humano'
                          ? 'Marcar como feito'
                          : 'Executar'}
                      {/* Sem isto, quem navega por lista de botões ouve
                          "Executar" cinco vezes e não sabe qual passo é qual. */}
                      <span className="u1-oculto">
                        {' '}
                        — passo {i + 1}: {passo.descricao}
                      </span>
                    </button>
                  )}

                  {podeDispensar && (
                    <button
                      type="button"
                      className="u1-passo__executar u1-passo__dispensar"
                      onClick={() => onDispensar(passo.id)}
                      disabled={dispensaEmVoo}
                    >
                      {/* O rótulo é a palavra do estado que ele produz:
                          "Dispensar" leva a "Dispensado", e a pessoa reconhece
                          na linha o que acabou de apertar. "Pular" ou "Adiar"
                          seriam mais macios e mentiriam — isto não deixa o
                          passo para depois, encerra. */}
                      {dispensaEmVoo ? 'Dispensando…' : 'Dispensar'}
                      {/* A frase inteira para quem escuta a tela: qual passo,
                          e que a decisão não se desfaz. Quem enxerga o plano
                          vê a linha riscada e entende sozinho; quem navega por
                          botões precisa ouvir antes de apertar. */}
                      <span className="u1-oculto">
                        {' '}
                        — passo {i + 1}: {passo.descricao}. Tira do plano; não
                        será executado, e isto não se desfaz.
                      </span>
                    </button>
                  )}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
